<template>
  <div class="metro-sign-generator" :class="{ 'component-hidden': hideComponent }">
    <!-- Google Fonts 思源黑体 -->
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="anonymous">
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;700;900&display=swap" rel="stylesheet">
    
    <!-- 页面中心标题 -->
    <div v-if="shouldShowAnimation" 
         class="center-title" 
         :class="{ 'fade-in': showTitle, 'fade-out': hideTitle }"
         @click="handleTitleClick">
      <h1>上海地铁40年</h1>
      <h2>乘坐体验升级了吗?</h2>
    </div>
    
    <!-- 动态标志流动展示区域 -->
    <div class="signs-flow-container" :class="{ 'dimmed': showTitle, 'accelerated': accelerateSigns }">
      <div 
        v-for="(sign, index) in currentSigns" 
        :key="`sign-${index}-${sign.id}`"
        class="sign-wrapper"
        :ref="el => { signRefs[sign.id] = el }"
        :style="{ 
          top: sign.top + 'px',
          zIndex: Math.floor(Math.random() * 10),
          opacity: isSignInTrack2(sign) && hideTrack2 && !isMobile ? 0 : 1,
          transition: 'opacity 1s ease-out'
        }"
      >
        <div 
          class="metro-sign" 
          :style="{ 
            width: sign.width + 'px', 
            height: sign.height + 'px',
            backgroundColor: sign.backgroundColor 
          }"
        >
          <svg 
            :width="sign.width" 
            :height="sign.height" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <!-- 线路标识元素 -->
            <g v-for="(element, elemIndex) in sign.elements" :key="`element-${elemIndex}`">
              <!-- 数字线路标识 - 严格按照原始库参数 -->
              <g v-if="element.type === 'NumberLine'">
                <!-- 单线路或多线路支持 -->
                <g v-for="(lineNumber, lineIndex) in element.lineNumbers" :key="`line-${lineIndex}`">
                  <!-- 边框 (白色) -->
                  <rect 
                    :x="element.x + lineIndex * element.size" 
                    :y="element.y" 
                    :width="Math.round(element.size * 8.5 / 10)" 
                    :height="element.size"
                    fill="white"
                    :stroke-width="element.size / 30"
                  />
                  <!-- 线路色彩背景 -->
                  <rect 
                    :x="element.x + lineIndex * element.size + element.size / 30" 
                    :y="element.y + element.size / 30" 
                    :width="Math.round(element.size * 8.5 / 10) - element.size / 15" 
                    :height="element.size - element.size / 15"
                    :fill="getLineColor(lineNumber).bg"
                  />
                  <!-- 线路号文字 -->
                  <text 
                    :x="element.x + lineIndex * element.size + Math.round(element.size * 8.5 / 10) / 2" 
                    :y="element.y + element.size / 2" 
                    :fill="getLineColor(lineNumber).text"
                    :font-size="lineNumber.length === 1 ? element.size / 1.2 : element.size / 1.5"
                    font-family="Arial, sans-serif"
                    text-anchor="middle"
                    dominant-baseline="central"
                  >
                    {{ lineNumber }}
                  </text>
                </g>
                <!-- "号线" 文字 -->
                <text 
                  :x="element.x + (element.lineNumbers?.length || 1) * element.size" 
                  :y="element.y + element.size * 2 / 3.5" 
                  :fill="element.color"
                  :font-size="element.size / 1.5"
                  font-family="SimHei, Microsoft YaHei, sans-serif"
                >
                  号线
                </text>
                <!-- 英文 "Line X" -->
                <text 
                  :x="element.x + (element.lineNumbers?.length || 1) * element.size" 
                  :y="element.y + element.size" 
                  :fill="element.color"
                  :font-size="element.size / 3"
                  font-family="Arial, sans-serif"
                >
                  Line {{ element.lineNumbers?.join(',') || '' }}
                </text>
              </g>

              <!-- 箭头元素 - 严格按照原始库绘制 -->
              <g v-if="element.type === 'Arrow'">
                <g :transform="`translate(${element.x}, ${element.y})`">
                  <!-- 箭头头部梯形1 -->
                  <polygon 
                    :points="getArrowPolygon1(element)"
                    :fill="element.color"
                  />
                  <!-- 箭头头部梯形2 -->
                  <polygon 
                    :points="getArrowPolygon2(element)"
                    :fill="element.color"
                  />
                  <!-- 箭头柄部矩形 -->
                  <rect 
                    :x="getArrowRectX(element)"
                    :y="getArrowRectY(element)"
                    :width="getArrowRectWidth(element)"
                    :height="getArrowRectHeight(element)"
                    :fill="element.color"
                  />
                </g>
              </g>

              <!-- 双语文本元素 - 严格按照原始库参数 -->
              <g v-if="element.type === 'BilingualText'">
                <!-- 中文文本 -->
                <text 
                  :x="getBilingualTextX(element, 'cn')" 
                  :y="element.y + element.size * 2 / 3 - element.size / 10" 
                  :fill="element.color"
                  :font-size="element.size / 1.5"
                  font-family="SimHei, Microsoft YaHei, sans-serif"
                  :text-anchor="element.align === 'center' ? 'middle' : (element.align === 'right' ? 'end' : 'start')"
                >
                  {{ element.text }}
                </text>
                <!-- 英文文本 -->
                <text 
                  :x="getBilingualTextX(element, 'en')" 
                  :y="element.y + element.size * 2 / 3 + element.size / 3" 
                  :fill="element.color"
                  :font-size="element.size / 3"
                  font-family="Arial, sans-serif"
                  :text-anchor="element.align === 'center' ? 'middle' : (element.align === 'right' ? 'end' : 'start')"
                >
                  {{ element.textEn }}
                </text>
              </g>

              <!-- 出口标识元素 - 严格按照原始库参数 -->
              <g v-if="element.type === 'ExitSign'">
                <!-- "出口" 中文 -->
                <text 
                  :x="element.x + element.size * 2 / 3" 
                  :y="element.y + element.size * 1 / 3 + 10" 
                  :fill="element.color"
                  :font-size="element.size / 1.5"
                  font-family="SimHei, Microsoft YaHei, sans-serif"
                  text-anchor="end"
                >
                  出口
                </text>
                <!-- "EXIT" 英文 -->
                <text 
                  :x="element.x + element.size * 2 / 3" 
                  :y="element.y + element.size / 2 + element.size / 3 + 10" 
                  :fill="element.color"
                  :font-size="element.size / 3"
                  font-family="Arial, sans-serif"
                  text-anchor="end"
                >
                  EXIT
                </text>
                <!-- 出口编号 -->
                <text 
                  :x="element.x + element.size" 
                  :y="element.y + element.size / 2 + element.size / 3" 
                  :fill="element.color"
                  :font-size="element.size * 1.2"
                  font-family="Arial, sans-serif"
                >
                  {{ element.code }}
                </text>
              </g>
            </g>
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { gsap } from 'gsap'

// 严格按照原始库的颜色配置
const metroColors: Record<string, { bg: string; text: string }> = {
  '1': { bg: '#E4002B', text: 'white' },
  '2': { bg: '#97D700', text: 'white' },
  '3': { bg: '#FFD100', text: 'black' },
  '4': { bg: '#461D84', text: 'white' },
  '5': { bg: '#944D9A', text: 'white' },
  '6': { bg: '#D40068', text: 'white' },
  '7': { bg: '#ED6F00', text: 'white' },
  '8': { bg: '#0094D8', text: 'white' },
  '9': { bg: '#87CAED', text: 'black' },
  '10': { bg: '#C6AFD4', text: 'black' },
  '11': { bg: '#871C2B', text: 'white' },
  '12': { bg: '#007A60', text: 'white' },
  '13': { bg: '#E999C0', text: 'black' },
  '14': { bg: '#626020', text: 'white' },
  '15': { bg: '#BCA886', text: 'black' },
  '16': { bg: '#98D1C0', text: 'black' },
  '17': { bg: '#BC796F', text: 'white' },
  '18': { bg: '#C4984F', text: 'white' },
  '19': { bg: '#F5AB78', text: 'black' },
  '20': { bg: '#009F65', text: 'white' },
  '21': { bg: '#F7AF00', text: 'black' },
  '22': { bg: '#5F376F', text: 'white' },
  '23': { bg: '#B0D478', text: 'black' },
  '26': { bg: '#5F67A9', text: 'white' }
}

// 获取所有可用的线路编号
const getAvailableLineNumbers = (): string[] => {
  return Object.keys(metroColors)
}

// 随机选择一条线路
const getRandomLineNumber = (): string => {
  const availableLines = getAvailableLineNumbers()
  const randomIndex = Math.floor(Math.random() * availableLines.length)
  return availableLines[randomIndex]
}

// 随机选择一条线路（排除4号线和26号线，用于站台标志）
const getRandomLineNumberForPlatform = (): string => {
  const availableLines = getAvailableLineNumbers().filter(line => line !== '4' && line !== '26')
  const randomIndex = Math.floor(Math.random() * availableLines.length)
  return availableLines[randomIndex]
}

// 真实的上海地铁线路起终点数据
const realMetroLines: Record<string, { start: {cn: string, en: string}, end: {cn: string, en: string} }> = {
  '1': { 
    start: { cn: '富锦路', en: 'Fujin Road' },
    end: { cn: '莘庄', en: 'Xinzhuang' }
  },
  '2': { 
    start: { cn: '浦东国际机场', en: 'Pudong International Airport' },
    end: { cn: '蟠祥路', en: 'Panxiang Road' }
  },
  '3': { 
    start: { cn: '上海南站', en: 'Shanghai South Railway Station' },
    end: { cn: '江杨北路', en: 'Jiangyang North Road' }
  },
  '5': { 
    start: { cn: '闵行开发区', en: 'Minhang Development Zone' },
    end: { cn: '奉贤新城', en: 'Fengxian Xincheng' }
  },
  '6': { 
    start: { cn: '东方体育中心', en: 'Oriental Sports Center' },
    end: { cn: '港城路', en: 'Gangcheng Road' }
  },
  '7': { 
    start: { cn: '花木路', en: 'Huamu Road' },
    end: { cn: '美兰湖', en: 'Meilan Lake' }
  },
  '8': { 
    start: { cn: '沈杜公路', en: 'Shendu Highway' },
    end: { cn: '市光路', en: 'Shiguang Road' }
  },
  '9': { 
    start: { cn: '松江南站', en: 'Songjiang South Railway Station' },
    end: { cn: '曹路', en: 'Cao Road' }
  },
  '10': { 
    start: { cn: '基隆路', en: 'Jilong Road' },
    end: { cn: '虹桥火车站', en: 'Hongqiao Railway Station' }
  },
  '11': { 
    start: { cn: '花桥', en: 'Huaqiao' },
    end: { cn: '迪士尼', en: 'Disney Resort' }
  },
  '12': { 
    start: { cn: '洞泾', en: 'Dongjing' },
    end: { cn: '金海路', en: 'Jinhai Road' }
  },
  '13': { 
    start: { cn: '丹桂路', en: 'Dangui Road' },
    end: { cn: '国家会展中心', en: 'National Exhibition and Convention Center' }
  },
  '14': { 
    start: { cn: '封浜', en: 'Fengbang' },
    end: { cn: '桂桥路', en: 'Guiqiao Road' }
  },
  '15': { 
    start: { cn: '顾村公园', en: 'Gucun Park' },
    end: { cn: '望园路', en: 'Wangyuan Road' }
  },
  '16': { 
    start: { cn: '滴水湖', en: 'Dishui Lake' },
    end: { cn: '龙阳路', en: 'Longyang Road' }
  },
  '17': { 
    start: { cn: '虹桥火车站', en: 'Hongqiao Railway Station' },
    end: { cn: '西岑', en: 'Xicen' }
  },
  '18': { 
    start: { cn: '航头', en: 'Hangtou' },
    end: { cn: '康文路', en: 'Kangwen Road' }
  },
  '19': { 
    start: { cn: '上海宝山站', en: 'Shanghai Baoshan Station' },
    end: { cn: '虹建路', en: 'Hongjian Road' }
  },
  '20': { 
    start: { cn: '北新园路', en: 'Beixinyuan Road' },
    end: { cn: '交通路', en: 'Jiaotong Road' }
  },
  '21': { 
    start: { cn: '东靖路', en: 'Dongjing Road' },
    end: { cn: '浦东3号航站楼', en: 'Pudong Terminal 3' }
  },
  '22': { 
    start: { cn: '裕安', en: 'Yuan' },
    end: { cn: '金吉路', en: 'Jinji Road' }
  },
  '23': { 
    start: { cn: '东川路', en: 'Dongchuan Road' },
    end: { cn: '上海体育场', en: 'Shanghai Stadium' }
  }
}

// 获取指定线路的真实起终点
const getRealLineTerminals = (lineNumber: string): { start: {cn: string, en: string}, end: {cn: string, en: string} } => {
  return realMetroLines[lineNumber] || {
    start: { cn: '未知起点', en: 'Unknown Start' },
    end: { cn: '未知终点', en: 'Unknown End' }
  }
}

// 常见站点名称库
const stationNames = [
  { cn: '人民广场', en: 'People\'s Square' },
  { cn: '南京东路', en: 'Nanjing East Road' },
  { cn: '陆家嘴', en: 'Lujiazui' },
  { cn: '世纪大道', en: 'Century Avenue' },
  { cn: '徐家汇', en: 'Xujiahui' },
  { cn: '静安寺', en: 'Jing\'an Temple' },
  { cn: '中山公园', en: 'Zhongshan Park' },
  { cn: '虹桥火车站', en: 'Hongqiao Railway Station' },
  { cn: '浦东国际机场', en: 'Pudong International Airport' },
  { cn: '上海火车站', en: 'Shanghai Railway Station' },
  { cn: '豫园', en: 'Yu Garden' },
  { cn: '南京西路', en: 'Nanjing West Road' },
  { cn: '淮海中路', en: 'Huaihai Middle Road' },
  { cn: '打浦桥', en: 'Dapuqiao' },
  { cn: '龙阳路', en: 'Longyang Road' },
  { cn: '张江高科', en: 'Zhangjiang Hi-Tech' },
  { cn: '金桥', en: 'Jinqiao' },
  { cn: '外滩', en: 'The Bund' },
  { cn: '田子坊', en: 'Tianzifang' },
  { cn: '新天地', en: 'Xintiandi' },
  { cn: '五角场', en: 'Wujiaochang' },
  { cn: '江湾体育场', en: 'Jiangwan Stadium' },
  { cn: '大学城', en: 'University Town' },
  { cn: '莘庄', en: 'Xinzhuang' },
  { cn: '富锦路', en: 'Fujin Road' },
  { cn: '宝山路', en: 'Baoshan Road' },
  { cn: '曲阜路', en: 'Qufu Road' },
  { cn: '老西门', en: 'Laoximen' },
  { cn: '黄陂南路', en: 'Huangpi South Road' },
  { cn: '常熟路', en: 'Changshu Road' }
]

// 随机获取站点名称
const getRandomStation = () => {
  return stationNames[Math.floor(Math.random() * stationNames.length)]
}

// 原始库常量
const EXIT_COLOR = '#FFD10F'

// 定义标志类型 - 严格按照原始库
interface MetroSign {
  id: number
  width: number
  height: number
  backgroundColor: string
  top: number
  speed: number
  animationDelay: number
  initialRight: number
  elements: SignElement[]
  createdAt?: number // 添加创建时间戳，用于清理
}

interface SignElement {
  type: 'NumberLine' | 'Arrow' | 'BilingualText' | 'ExitSign'
  x: number
  y: number
  size: number
  color: string
  // NumberLine特有属性
  lineNumbers?: string[]
  // Arrow特有属性
  direction?: 'up' | 'down' | 'left' | 'right'
  thickness?: number
  // BilingualText特有属性
  text?: string
  textEn?: string
  align?: 'left' | 'right' | 'center'
  textWidth?: number
  // ExitSign特有属性
  code?: string
}

const currentSigns = ref<MetroSign[]>([])
const signsPendingCleanup = ref<number[]>([]) // 待清理的标志ID队列
const signIdCounter = ref(0)
const signRefs = ref<Record<number, any>>({})

// 获取线路颜色 - 严格按照原始库
const getLineColor = (lineNumber: string) => {
  return metroColors[lineNumber] || { bg: '#E4002B', text: 'white' }
}

// 计算箭头多边形坐标 - 严格按照原始库算法
const getArrowPolygon1 = (element: SignElement): string => {
  const { size, direction, thickness = size / 4 } = element
  const centerX = size / 2
  const centerY = size / 2
  
  let points: number[][] = []
  
  if (direction === 'left') {
    points = [
      [0, centerY],
      [thickness, centerY],
      [thickness + size / 3, centerY - size / 3],
      [size / 3, centerY - size / 3]
    ]
  } else if (direction === 'right') {
    points = [
      [size, centerY],
      [size - thickness, centerY],
      [size - thickness - size / 3, centerY - size / 3],
      [size - size / 3, centerY - size / 3]
    ]
  } else if (direction === 'up') {
    points = [
      [centerX, 0],
      [centerX, thickness],
      [centerX - size / 3, thickness + size / 3],
      [centerX - size / 3, size / 3]
    ]
  } else if (direction === 'down') {
    points = [
      [centerX, size],
      [centerX, size - thickness],
      [centerX - size / 3, size - thickness - size / 3],
      [centerX - size / 3, size - size / 3]
    ]
  }
  
  return points.map(p => p.join(',')).join(' ')
}

const getArrowPolygon2 = (element: SignElement): string => {
  const { size, direction, thickness = size / 4 } = element
  const centerX = size / 2
  const centerY = size / 2
  
  let points: number[][] = []
  
  if (direction === 'left') {
    points = [
      [0, centerY],
      [thickness, centerY],
      [thickness + size / 3, centerY + size / 3],
      [size / 3, centerY + size / 3]
    ]
  } else if (direction === 'right') {
    points = [
      [size, centerY],
      [size - thickness, centerY],
      [size - thickness - size / 3, centerY + size / 3],
      [size - size / 3, centerY + size / 3]
    ]
  } else if (direction === 'up') {
    points = [
      [centerX, 0],
      [centerX, thickness],
      [centerX + size / 3, thickness + size / 3],
      [centerX + size / 3, size / 3]
    ]
  } else if (direction === 'down') {
    points = [
      [centerX, size],
      [centerX, size - thickness],
      [centerX + size / 3, size - thickness - size / 3],
      [centerX + size / 3, size - size / 3]
    ]
  }
  
  return points.map(p => p.join(',')).join(' ')
}

// 计算箭头矩形坐标 - 严格按照原始库算法
const getArrowRectX = (element: SignElement): number => {
  const { size, direction, thickness = size / 4 } = element
  const centerX = size / 2
  
  if (direction === 'left') {
    return thickness / 2
  } else if (direction === 'right') {
    return 0
  } else {
    return centerX - thickness / 2.828
  }
}

const getArrowRectY = (element: SignElement): number => {
  const { size, direction, thickness = size / 4 } = element
  const centerY = size / 2
  
  if (direction === 'up') {
    return thickness / 2
  } else if (direction === 'down') {
    return 0
  } else {
    return centerY - thickness / 2.828
  }
}

const getArrowRectWidth = (element: SignElement): number => {
  const { size, direction, thickness = size / 4 } = element
  
  if (direction === 'left') {
    return size - thickness / 2
  } else if (direction === 'right') {
    return size - thickness / 2
  } else {
    return thickness / 1.414
  }
}

const getArrowRectHeight = (element: SignElement): number => {
  const { size, direction, thickness = size / 4 } = element
  
  if (direction === 'up') {
    return size - thickness / 2
  } else if (direction === 'down') {
    return size - thickness / 2
  } else {
    return thickness / 1.414
  }
}

// 计算双语文本X坐标 - 严格按照原始库对齐算法
const getBilingualTextX = (element: SignElement, type: 'cn' | 'en'): number => {
  const { x, align, textWidth = 0, size } = element
  const maxWidth = textWidth
  
  if (align === 'left') {
    return type === 'en' ? x + size / 30 : x
  } else if (align === 'right') {
    return type === 'en' ? x + maxWidth - size / 30 : x + maxWidth
  } else if (align === 'center') {
    return x + maxWidth / 2
  }
  
  return x
}

// 生成线路标识 - 支持任意线路编号和箭头方向
const generateLineSign = (lineNumber?: string, direction: 'left' | 'right' | 'up' | 'down' = 'right'): MetroSign => {
  const selectedLineNumber = lineNumber || getRandomLineNumber()
  
  const elements: SignElement[] = [
    {
      type: 'NumberLine',
      x: 10,
      y: 10,
      size: 108,
      lineNumbers: [selectedLineNumber],
      color: 'white'
    },
    {
      type: 'Arrow',
      x: 512 - 108 - 10,
      y: (128 - 108) / 2,
      size: 108,
      direction: direction,
      thickness: 108 / 4,
      color: 'white'
    }
  ]

  return {
    id: signIdCounter.value++,
    width: 512,
    height: 128,
    backgroundColor: 'black',
    top: 0,
    speed: 18,
    animationDelay: 0,
    initialRight: 0,
    elements
  }
}

// 兼容旧函数名
const generateLine3RightSign = (): MetroSign => {
  return generateLineSign('3', 'right')
}

// 生成换乘标识 - 支持任意线路组合和箭头方向，必须有三条线路
const generateTransferSign = (lineNumbers?: string[], direction: 'left' | 'right' | 'up' | 'down' = 'up'): MetroSign => {
  // 如果没有指定线路，随机选择3条线路
  let selectedLines: string[]
  if (lineNumbers && lineNumbers.length > 0) {
    selectedLines = lineNumbers
  } else {
    const availableLines = getAvailableLineNumbers()
    selectedLines = []
    // 固定生成3条线路
    for (let i = 0; i < 3; i++) {
      let randomLine: string
      do {
        randomLine = availableLines[Math.floor(Math.random() * availableLines.length)]
      } while (selectedLines.includes(randomLine))
      selectedLines.push(randomLine)
    }
    selectedLines.sort((a, b) => parseInt(a) - parseInt(b)) // 按数字排序
  }
  
  const padding = Math.floor(128 / 12)  // = 10，与原仓库一致
  
  const elements: SignElement[] = [
    {
      type: 'Arrow',
      x: padding,  // 10
      y: (128 - 108) / 2,
      size: 108,
      direction: direction,
      thickness: 108 / 4,
      color: 'white'
    },
    {
      type: 'NumberLine',
      x: padding + 108 + padding, // 10 + 108 + 10 = 128
      y: (128 - 108) / 2,
      size: 108,
      lineNumbers: selectedLines,
      color: 'white'
    }
  ]

  return {
    id: signIdCounter.value++,
    width: 680, // 512 + 128
    height: 128,
    backgroundColor: 'black',
    top: 0,
    speed: 18,
    animationDelay: 0,
    initialRight: 0,
    elements
  }
}

// 兼容旧函数名
const generateCenturyAvenueSign = (): MetroSign => {
  return generateTransferSign(['4', '6', '9'], 'up')
}

// 生成站台标识 - 支持任意线路和站点
const generatePlatformSign = (lineNumber?: string, leftStation?: {cn: string, en: string}, rightStation?: {cn: string, en: string}): MetroSign => {
  // 随机选择线路编号（排除4号线和26号线）
  const selectedLineNumber = lineNumber || getRandomLineNumberForPlatform()
  
  // 获取该线路的真实起终点
  const realTerminals = getRealLineTerminals(selectedLineNumber)
  
  // 如果没有指定左右站点，使用真实的起终点
  const leftStationInfo = leftStation || realTerminals.start
  const rightStationInfo = rightStation || realTerminals.end
  
  const elements: SignElement[] = [
    // 左侧箭头 (left对齐)
    {
      type: 'Arrow',
      x: 10,
      y: (128 - 108) / 2,
      size: 108,
      direction: 'left',
      thickness: 108 / 4,
      color: 'white'
    },
    // 左侧文本 (left对齐，紧跟箭头)
    {
      type: 'BilingualText',
      x: 10 + 108 + 10,
      y: (128 - 108) / 2,
      size: 108,
      text: `往${leftStationInfo.cn}`,
      textEn: `To ${leftStationInfo.en}`,
      color: 'white',
      align: 'left',
      textWidth: 200
    },
    // 中央线路标识 (center对齐)
    {
      type: 'NumberLine',
      x: 1024 - 75, // 2048/2 - (线路框宽度/2)
      y: (128 - 108) / 2,
      size: 108,
      lineNumbers: [selectedLineNumber],
      color: 'white'
    },
    // 右侧文本 (right对齐)
    {
      type: 'BilingualText',
      x: 2048 - 10 - 108 - 10 - 200, // 从右侧计算
      y: (128 - 108) / 2,
      size: 108,
      text: `往${rightStationInfo.cn}`,
      textEn: `To ${rightStationInfo.en}`,
      color: 'white',
      align: 'right',
      textWidth: 200
    },
    // 右侧箭头 (right对齐)
    {
      type: 'Arrow',
      x: 2048 - 10 - 108,
      y: (128 - 108) / 2,
      size: 108,
      direction: 'right',
      thickness: 108 / 4,
      color: 'white'
    }
  ]

  return {
    id: signIdCounter.value++,
    width: 2048,
    height: 128,
    backgroundColor: 'black',
    top: 0,
    speed: 18,
    animationDelay: 0,
    initialRight: 0,
    elements
  }
}

// 生成出口标识 - 支持任意出口编号和箭头方向
const generateExitSign = (exitCode?: string, direction: 'left' | 'right' | 'up' | 'down' = 'up'): MetroSign => {
  // 如果没有指定出口编号，随机生成
  let selectedExitCode: string
  if (exitCode) {
    selectedExitCode = exitCode
  } else {
    // 随机生成出口编号模式 - 只保留"数字+范围"和"三个数字"两种模式，数字限制小于10
    const patterns = [
      // 数字+范围模式，如：1,5-9（确保第一个数字不在范围内）
      () => {
        // 先生成范围
        const rangeStart = Math.floor(Math.random() * 6 + 2) // 2-7
        const rangeEnd = Math.min(rangeStart + Math.floor(Math.random() * 3 + 1), 9) // +1到+3，但不超过9
        
        // 生成第一个数字，确保不在范围内
        let firstNum: number
        do {
          firstNum = Math.floor(Math.random() * 9 + 1) // 1-9
        } while (firstNum >= rangeStart && firstNum <= rangeEnd) // 确保不在范围内
        
        return `${firstNum},${rangeStart}-${rangeEnd}`
      },
      // 三个数字模式，如：1,5,8
      () => {
        const nums: number[] = []
        for (let i = 0; i < 3; i++) {
          let num: number
          do {
            num = Math.floor(Math.random() * 9 + 1) // 1-9，限制小于10
          } while (nums.includes(num))
          nums.push(num)
        }
        nums.sort((a, b) => a - b) // 按数字大小排序
        return nums.join(',')
      }
    ]
    const randomPattern = patterns[Math.floor(Math.random() * patterns.length)]
    selectedExitCode = randomPattern()
  }
  
  const elements: SignElement[] = [
    {
      type: 'ExitSign',
      x: 512 - 300, // center对齐 - 元素宽度的一半
      y: (256 - 108) / 2,
      size: 108,
      code: selectedExitCode,
      color: EXIT_COLOR,
      textWidth: 120
    },
    {
      type: 'Arrow',
      x: 512 + 180, // center对齐 + 偏移
      y: (256 - 108) / 2 - 10,
      size: 108,
      direction: direction,
      thickness: 108 / 4,
      color: EXIT_COLOR
    }
  ]

  return {
    id: signIdCounter.value++,
    width: 960,
    height: 256,
    backgroundColor: 'black',
    top: 0,
    speed: 18,
    animationDelay: 0,
    initialRight: 0,
    elements
  }
}

// 弹幕轨道系统
interface DanmakuTrack {
  id: number
  y: number
  height: number
  signs: MetroSign[]
}

const tracks = ref<DanmakuTrack[]>([])
const trackHeight = ref(120) // 每个轨道的高度（响应式）
const trackSpacing = ref(10) // 轨道间距（响应式）
const signSpacing = 100 // 同轨道标志间距

// 根据屏幕大小动态调整轨道参数
const adjustTrackParameters = () => {
  const screenHeight = window.innerHeight
  const screenWidth = window.innerWidth
  
  // 根据屏幕高度调整轨道高度和间距
  if (screenHeight <= 600) {
    // 小屏幕（如手机横屏）
    trackHeight.value = 60
    trackSpacing.value = 8
  } else if (screenHeight <= 900) {
    // 中等屏幕（如平板）
    trackHeight.value = 70
    trackSpacing.value = 10
  } else {
    // 大屏幕（如桌面）
    trackHeight.value = 80
    trackSpacing.value = 12
  }
  
}

// 窗口大小变化监听
let resizeTimeout: number | null = null

// 响应式重新计算轨道
const recalculateTracks = () => {
  
  // 调整轨道参数
  adjustTrackParameters()
  
  // 清除现有动画
  currentSigns.value.forEach(sign => {
    const element = signRefs.value[sign.id]
    if (element) {
      gsap.killTweensOf(element)
    }
    // 也清理可能的timeline动画
    const timeline = gsap.getById(`timeline-${sign.id}`)
    if (timeline) {
      timeline.kill()
    }
  })
  
  // 清空当前标志
  currentSigns.value = []
  signRefs.value = {}
  
  // 重新初始化轨道（包括预生成标志）
  initializeDanmakuTracks()
}

// 动画控制相关的响应式数据
const hideTrack2 = ref(false) // 控制轨道2的显示/隐藏
const showTitle = ref(false) // 控制中心标题的显示
const accelerateSigns = ref(false) // 控制标志流加速
const hideTitle = ref(false) // 控制标题隐藏
const scrollTriggerActivated = ref(false) // 滚动触发器是否已激活
const hideComponent = ref(false) // 控制整个组件的隐藏
const preventScroll = ref(false) // 控制是否阻止滚动
const shouldShowAnimation = ref(false) // 控制是否显示动画
const isMobile = ref(false) // 标识是否为移动端设备

// 定义 emit 事件
const emit = defineEmits<{
  'transition-complete': []
}>()

// 暂停所有动画
const pauseAllAnimations = () => {
  currentSigns.value.forEach(sign => {
    const element = signRefs.value[sign.id]
    if (element) {
      // 获取所有类型的动画（包括timeline）
      const tweens = gsap.getTweensOf(element)
      const timelines = gsap.getById(`timeline-${sign.id}`) // 如果我们给timeline设置了ID
      
      tweens.forEach(tween => tween.pause())
      if (timelines) {
        timelines.pause()
      }
    }
  })
}

// 恢复所有动画
const resumeAllAnimations = () => {
  currentSigns.value.forEach(sign => {
    const element = signRefs.value[sign.id]
    if (element) {
      // 获取所有类型的动画（包括timeline）
      const tweens = gsap.getTweensOf(element)
      const timelines = gsap.getById(`timeline-${sign.id}`) // 如果我们给timeline设置了ID
      
      tweens.forEach(tween => tween.resume())
      if (timelines) {
        timelines.resume()
      }
    }
  })
}

// 判断标志是否在轨道2中
const isSignInTrack2 = (sign: MetroSign): boolean => {
  if (tracks.value.length <= 2) return false
  const track2 = tracks.value[2] // 轨道2（第3条轨道，index为2）
  return track2.signs.some(trackSign => trackSign.id === sign.id)
}

// 初始化弹幕轨道
const initializeDanmakuTracks = () => {
  // 首先调整轨道参数
  adjustTrackParameters()
  
  // 计算可以容纳的轨道数量
  const availableHeight = window.innerHeight
  const trackTotalHeight = trackHeight.value + trackSpacing.value
  const trackCount = Math.floor(availableHeight / trackTotalHeight)
  
  
  const newTracks: DanmakuTrack[] = []
  
  for (let i = 0; i < trackCount; i++) {
    // 修正计算：第一个轨道从顶部开始，后续轨道间隔 trackTotalHeight
    const trackY = i * trackTotalHeight
    const track: DanmakuTrack = {
      id: i,
      y: trackY,
      height: 50 + trackHeight.value,
      signs: []
    }
    
    newTracks.push(track)
    
    
    // 计算与下一个轨道的实际间距
    if (i > 0) {
      const prevTrackBottom = newTracks[i-1].y + newTracks[i-1].height
      const currentTrackTop = trackY
      const actualSpacing = currentTrackTop - prevTrackBottom
    }
  }
  
  tracks.value = newTracks
  
  // 延迟生成初始标志，确保DOM元素已准备好
  setTimeout(() => {
    initializeTrackSigns()
  }, 100)
}

// 为轨道预先生成标志
const initializeTrackSigns = () => {
  const screenWidth = window.innerWidth
  
  
  tracks.value.forEach((track, trackIndex) => {
    // 计算每个轨道需要预先生成多少个标志
    // 根据屏幕宽度和标志间距估算
    const estimatedSignsOnScreen = Math.ceil(screenWidth / (signSpacing * 0.8)) + 2 // 多生成2个确保覆盖
    
    
    // 从屏幕右边缘开始，向左依次放置标志
    let currentX = screenWidth + 100 // 从屏幕右边缘外一点开始
    
    for (let signIndex = 0; signIndex < estimatedSignsOnScreen; signIndex++) {
      const sign = createRandomSign()
      
      // 调整标志高度适应轨道
      const maxHeight = track.height * 0.8
      if (sign.height > maxHeight) {
        const scaleFactor = maxHeight / sign.height
        sign.height = Math.round(sign.height * scaleFactor)
        sign.width = Math.round(sign.width * scaleFactor)
        
        // 缩放所有元素
        sign.elements.forEach(element => {
          element.x = Math.round(element.x * scaleFactor)
          element.y = Math.round(element.y * scaleFactor)
          element.size = Math.round(element.size * scaleFactor)
          if (element.thickness) {
            element.thickness = Math.round(element.thickness * scaleFactor)
          }
          if (element.textWidth) {
            element.textWidth = Math.round(element.textWidth * scaleFactor)
          }
        })
      }
      
      // 设置标志ID和位置
      sign.id = signIdCounter.value++
      sign.top = track.y + (track.height - sign.height) / 2
      
      // 计算这个标志的X位置（向左递推）
      currentX = currentX - sign.width - signSpacing
      
      // 添加到轨道和全局数组
      track.signs.push(sign)
      currentSigns.value.push(sign)
      
    }
  })
  
  
  // 等待一个渲染周期后启动动画
  nextTick(() => {
    setTimeout(() => {
      
      tracks.value.forEach((track, trackIndex) => {
        track.signs.forEach((sign, signIndex) => {
          // 重新计算每个标志的X位置
          const screenWidth = window.innerWidth
          let currentX = screenWidth + 100
          for (let i = 0; i <= signIndex; i++) {
            if (i === signIndex) break
            currentX = currentX - track.signs[i].width - signSpacing
          }
          currentX = currentX - sign.width - signSpacing
          
          // 计算渐入延迟：轨道间隔0.05秒，同轨道标志间隔0.03秒
          const fadeInDelay = trackIndex * 0.05 + signIndex * 0.03
          
          // 启动带渐入效果的动画
          startDanmakuAnimationWithFadeIn(sign.id, currentX, track, fadeInDelay)
        })
      })
      
    }, 50)
  })
}

// 获取轨道速度（像素/秒）- 不同轨道有不同速度
const getTrackSpeed = (trackId: number): number => {
  // 基础速度
  const baseSpeed = accelerateSigns.value ? 400 : 150 // 加速时速度增加
  
  // 根据轨道ID创建速度变化，让不同轨道有不同的速度
  const speedVariation = (trackId % 3) * 30 // 0, 30, 60的速度差异
  const randomVariation = (Math.sin(trackId * 1.618) * 20) // 基于黄金比例的伪随机变化
  
  return baseSpeed + speedVariation + randomVariation
}

// 检测是否为移动端设备
const isMobileDevice = (): boolean => {
  return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

// 检查页面初始状态
const checkInitialScrollPosition = () => {
  const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop
  
  // 检查是否有保存的滚动位置（从localStorage读取，持久保存）
  const savedScrollPosition = localStorage.getItem('shmetro_scroll_position')
  let actualScrollPosition = scrollY
  
  if (savedScrollPosition && savedScrollPosition !== '0') {
    actualScrollPosition = parseInt(savedScrollPosition, 10)
  } else {
  }
  
  // 如果页面不在顶端（允许5px的误差），不显示动画
  if (actualScrollPosition > 5) {
    shouldShowAnimation.value = false
    // 直接通知父组件显示主内容
    setTimeout(() => {
      emit('transition-complete')
      hideComponent.value = true
    }, 100)
    return false
  } else {
    shouldShowAnimation.value = true
    // 立即开始限制滚动
    preventScroll.value = true
    document.body.style.overflow = 'hidden'
    return true
  }
}

// 阻止滚动事件
const preventScrollEvent = (e: Event) => {
  if (preventScroll.value) {
    e.preventDefault()
    e.stopPropagation()
    return false
  }
}

// 阻止键盘滚动事件
const preventKeyboardScroll = (e: KeyboardEvent) => {
  if (preventScroll.value && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || 
      e.key === ' ' || e.key === 'PageUp' || e.key === 'PageDown' || 
      e.key === 'Home' || e.key === 'End')) {
    e.preventDefault()
    e.stopPropagation()
    return false
  }
}

// 滚动监听函数
const handleScroll = () => {
  // 如果不应该显示动画，直接返回
  if (!shouldShowAnimation.value) return
  
  // 只有在标题显示后且滚动触发器未激活时才监听滚动
  if (!showTitle.value || scrollTriggerActivated.value) return
  
  const scrollY = window.scrollY
  const triggerDistance = 100 // 向下滚动100px触发
  
  if (scrollY >= triggerDistance) {
    triggerTransition()
  }
}

// 触发切换到主内容的函数（可以由滚动或点击触发）
const triggerTransition = () => {
  if (scrollTriggerActivated.value) return // 防止重复触发
  
  scrollTriggerActivated.value = true
  
  // 停止渲染新标志
  if ((window as any).danmakuInterval) {
    clearInterval((window as any).danmakuInterval)
    delete (window as any).danmakuInterval
  }
  
  // 加速现有标志
  accelerateSigns.value = true
  accelerateExistingSigns()
  
  // 等待标志离开页面后隐藏标题并通知父组件显示主内容
  setTimeout(() => {
    hideTitle.value = true
    
    setTimeout(() => {
      // 通知父组件可以显示主内容了
      emit('transition-complete')
      
      // 清除保存的滚动位置，这样下次从顶部访问时可以重新播放动画
      localStorage.removeItem('shmetro_scroll_position')
      
      // 恢复滚动能力
      document.body.style.overflow = 'auto'
      preventScroll.value = false
      
      // 再等待一点时间确保主内容开始显示后再隐藏组件
      setTimeout(() => {
        hideComponent.value = true
      }, 200) // 0.2秒后隐藏组件
    }, 500) // 标题隐藏动画0.5秒后通知父组件
  }, 1000) // 1秒后开始隐藏标题（等待标志基本离开页面）
}

// 处理标题点击事件
const handleTitleClick = () => {
  // 如果不应该显示动画或标题还未完全显示，直接返回
  if (!shouldShowAnimation.value || !showTitle.value || scrollTriggerActivated.value) return
  
  triggerTransition()
}

// 加速现有标志的函数
const accelerateExistingSigns = () => {
  currentSigns.value.forEach(sign => {
    const element = signRefs.value[sign.id]
    if (element) {
      try {
        // 获取当前位置
        const currentX = gsap.getProperty(element, "x") as number
        
        // 如果标志还在屏幕内或附近，重新创建加速动画
        if (currentX > -sign.width - 200) {
          // 停止当前动画
          gsap.killTweensOf(element)
          
          // 创建加速动画
          const endX = -sign.width - 50
          const distance = currentX - endX
          const newSpeed = getTrackSpeed(0) * 2 // 使用2倍速度
          const duration = Math.max(distance / newSpeed, 0.5) // 最少0.5秒
          
          gsap.to(element, {
            x: endX,
            duration: duration,
            ease: "power2.in" // 使用加速缓动
          })
          
        }
      } catch (error) {
        // Error handled silently
      }
    }
  })
}

// 在指定轨道添加标志
const addSignToTrack = (track: DanmakuTrack, sign: MetroSign) => {
  // 为标志生成唯一数字ID
  sign.id ++
  
  // 设置标志位置
  sign.top = track.y + (track.height - sign.height) / 2
  sign.initialRight = 0 // 临时值，GSAP会重新设置
  
  // 计算新标志的正确开始位置
  let startX = window.innerWidth // 默认从屏幕右边缘开始
  
  // 找到轨道上最右侧标志的位置
  let rightmostRightX = -Infinity
  track.signs.forEach(existingSign => {
    const element = signRefs.value[existingSign.id]
    if (element) {
      try {
        const currentX = gsap.getProperty(element, "x") as number
        const signRightX = currentX + existingSign.width
        if (signRightX > rightmostRightX) {
          rightmostRightX = signRightX
        }
      } catch (error) {
        // Error handled silently
      }
    }
  })
  
  // 如果轨道有标志，新标志应该在最右侧标志后面
  if (rightmostRightX !== -Infinity) {
    startX = Math.max(rightmostRightX + signSpacing, window.innerWidth)
  }
  
  
  // 添加到轨道和全局数组
  track.signs.push(sign)
  currentSigns.value.push(sign)
  
  // 启动弹幕动画
  nextTick(() => {
    startDanmakuAnimation(sign.id, startX, track)
  })
}

// 弹幕动画（从右到左匀速移动）
const startDanmakuAnimation = (signId: number, startX: number, track: DanmakuTrack) => {
  const element = signRefs.value[signId]
  if (!element) {
    return
  }
  
  const sign = currentSigns.value.find(s => s.id === signId)
  if (!sign) {
    return
  }
  
  
  // 设置初始位置 - startX是从右边缘开始的位置
  gsap.set(element, {
    x: startX,
    y: sign.top,
    opacity: 1
  })
  
  // 计算移动距离和时间
  const endX = -sign.width - 50  // 完全移出左边缘
  const distance = startX - endX
  const duration = distance / getTrackSpeed(track.id)
  
  
  // 匀速向左移动
  const tween = gsap.to(element, {
    x: endX,
    duration: duration,
    ease: "none"
  })
}

// 带渐入效果的弹幕动画（用于预加载的标志）
const startDanmakuAnimationWithFadeIn = (signId: number, startX: number, track: DanmakuTrack, delay: number = 0) => {
  const element = signRefs.value[signId]
  if (!element) {
    return
  }
  
  const sign = currentSigns.value.find(s => s.id === signId)
  if (!sign) {
    return
  }
  
  
  // 设置初始位置和透明度
  gsap.set(element, {
    x: startX,
    y: sign.top,
    opacity: 0 // 初始透明
  })
  
  // 计算移动距离和时间
  const endX = -sign.width - 50  // 完全移出左边缘
  const distance = startX - endX
  const duration = distance / getTrackSpeed(track.id)
  
  // 创建时间线动画，设置ID便于管理
  const timeline = gsap.timeline({ 
    delay,
    id: `timeline-${signId}` // 设置唯一ID
  })
  
  // 渐入效果（0.8秒内从透明到不透明，使用更平滑的缓动）
  timeline.to(element, {
    opacity: 1,
    duration: 0.8,
    ease: "power2.out"
  })
  
  // 同时开始移动动画
  timeline.to(element, {
    x: endX,
    duration: duration,
    ease: "none"
  }, 0) // 0表示与前一个动画同时开始
}


// 持续添加新弹幕
const continuousDanmakuGeneration = () => {
  const interval = setInterval(() => {
    // 检查每个轨道是否需要新标志
    tracks.value.forEach(track => {
      // 计算轨道上所有存活标志的右边界位置
      let rightmostRightX = -Infinity
      let validSignsCount = 0
      
      // 遍历轨道上的所有标志
      track.signs.forEach(sign => {
        const element = signRefs.value[sign.id]
        if (element) {
          try {
            const currentX = gsap.getProperty(element, "x") as number
            const signRightX = currentX + sign.width // 标志的右边界
            
            // 记录最右侧的标志位置（不管是否在屏幕内）
            if (signRightX > rightmostRightX) {
              rightmostRightX = signRightX
            }
            validSignsCount++
            
          } catch (error) {
            // 如果元素已被清理，忽略错误
          }
        }
      })
      
      const screenRightEdge = window.innerWidth
      
      // 渲染时机判断逻辑：
      // 当轨道最右侧标志的右边界距离页面右边缘等于间距值时，触发新标志渲染
      let needsNewSign = false
      
      if (validSignsCount === 0) {
        // 轨道完全为空，立即需要新标志
        needsNewSign = true
      } else {
        // 计算最右侧标志右边界到页面右边缘的距离
        const distanceToScreenEdge = screenRightEdge - rightmostRightX
        
        // 当距离大于等于间距值时，可以渲染新标志
        if (distanceToScreenEdge >= signSpacing) {
          needsNewSign = true
        }
      }
      
      if (needsNewSign) {
        const sign = createRandomSign()
        
        // 调整标志高度适应轨道
        const maxHeight = track.height * 0.8
        if (sign.height > maxHeight) {
          const scaleFactor = maxHeight / sign.height
          sign.height = Math.round(sign.height * scaleFactor)
          sign.width = Math.round(sign.width * scaleFactor)
          
          // 缩放所有元素
          sign.elements.forEach(element => {
            element.x = Math.round(element.x * scaleFactor)
            element.y = Math.round(element.y * scaleFactor)
            element.size = Math.round(element.size * scaleFactor)
            if (element.thickness) {
              element.thickness = Math.round(element.thickness * scaleFactor)
            }
            if (element.textWidth) {
              element.textWidth = Math.round(element.textWidth * scaleFactor)
            }
          })
        }
        
        addSignToTrack(track, sign)
      }
    })
  }, 100) // 更频繁检查，从200ms改为100ms，提高生成频率
  
  // 保存定时器ID以便清理
  ;(window as any).danmakuInterval = interval
  
  return interval
}

// 创建随机标志的通用函数
const createRandomSign = (): MetroSign => {
  const signGenerators = [
    () => generateLineSign(), // 随机线路标识
    () => generateLineSign(undefined, 'left'), // 向左的线路标识
    () => generateTransferSign(), // 随机换乘标识
    () => generateTransferSign(undefined, 'down'), // 向下的换乘标识
    () => generatePlatformSign(), // 随机站台标识
    () => generateExitSign(), // 随机出口标识
    () => generateExitSign(undefined, 'left'), // 向左的出口标识
    () => generateExitSign(undefined, 'down'), // 向下的出口标识
  ]
  
  const randomGenerator = signGenerators[Math.floor(Math.random() * signGenerators.length)]
  const sign = randomGenerator()
  
  // 移除随机缩放功能，直接使用原始尺寸
  sign.createdAt = Date.now()
  return sign
}

onMounted(() => {
  try {
    
    // 初始化移动端检测
    isMobile.value = isMobileDevice()
    
    // 检查页面初始滚动位置
    if (!checkInitialScrollPosition()) {
      // 如果不在顶端，直接返回，不初始化动画
      return
    }
    
    // 防止重复初始化
    if ((window as any).danmakuInterval) {
      return
    }
    
    // 1. 初始化弹幕轨道系统
    initializeDanmakuTracks()
    
    // 2. 添加窗口大小变化监听器
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout)
      }
      resizeTimeout = window.setTimeout(() => {
        // 重新检测移动端状态
        isMobile.value = isMobileDevice()
        recalculateTracks()
      }, 300) // 防抖，300ms后重新计算
    }
    
    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleScroll) // 添加滚动监听
    window.addEventListener('wheel', preventScrollEvent, { passive: false }) // 添加滚轮阻止
    window.addEventListener('touchmove', preventScrollEvent, { passive: false }) // 添加触摸滚动阻止
    window.addEventListener('keydown', preventKeyboardScroll, { passive: false }) // 添加键盘滚动阻止
    
    // 保存事件监听器引用以便清理
    ;(window as any).resizeHandler = handleResize
    ;(window as any).scrollHandler = handleScroll
    
    // 3. 启动持续的弹幕生成
    setTimeout(() => {
      continuousDanmakuGeneration()
    }, 500) // 减少延迟，从2秒改为0.5秒
    
    // 5. 启动轨道2隐藏定时器：标志流开始滚动1秒后淡出轨道2（仅在桌面端且应该显示动画时）
    if (shouldShowAnimation.value && !isMobileDevice()) {
      setTimeout(() => {
        hideTrack2.value = true
        
        // 轨道2淡出完成后(1秒淡出时间后)再淡入标题
        setTimeout(() => {
          showTitle.value = true
        }, 1000) // 等待轨道2淡出完成
      }, 2500) // 1秒后开始淡出轨道2（500ms启动延迟 + 1000ms）
    } else if (shouldShowAnimation.value && isMobileDevice()) {
      // 移动端直接显示标题，不隐藏轨道2
      setTimeout(() => {
        showTitle.value = true
      }, 1500) // 移动端更快显示标题
    }
    
  } catch (error) {
    // Error handled silently
  }
})

// 重新启动动画的方法
const restartAnimation = () => {
  // 重新检测移动端状态
  isMobile.value = isMobileDevice()
  
  // 重置所有状态
  hideTrack2.value = false
  showTitle.value = false
  accelerateSigns.value = false
  hideTitle.value = false
  scrollTriggerActivated.value = false
  hideComponent.value = false
  preventScroll.value = false
  shouldShowAnimation.value = true
  
  // 清除保存的滚动位置，确保动画重新播放
  localStorage.removeItem('shmetro_scroll_position')
  
  // 清理现有的标志和动画
  currentSigns.value.forEach(sign => {
    const element = signRefs.value[sign.id]
    if (element) {
      gsap.killTweensOf(element)
    }
  })
  
  // 清空当前标志
  currentSigns.value = []
  signRefs.value = {}
  
  // 重新初始化
  setTimeout(() => {
    initializeDanmakuTracks()
    
    // 重新启动标志流和动画序列
    setTimeout(() => {
      continuousDanmakuGeneration()
    }, 500)
    
    // 重新启动轨道2隐藏定时器（仅桌面端）
    if (!isMobileDevice()) {
      setTimeout(() => {
        hideTrack2.value = true
        
        // 重新显示标题
        setTimeout(() => {
          showTitle.value = true
        }, 1000)
      }, 2500)
    } else {
      // 移动端直接显示标题
      setTimeout(() => {
        showTitle.value = true
      }, 1500)
    }
  }, 100)
}

// 暴露方法给父组件
defineExpose({
  restartAnimation
})

onUnmounted(() => {
  
  // 清理弹幕生成定时器
  if ((window as any).danmakuInterval) {
    clearInterval((window as any).danmakuInterval)
    delete (window as any).danmakuInterval
  }
  
  // 清理窗口大小变化监听器
  if ((window as any).resizeHandler) {
    window.removeEventListener('resize', (window as any).resizeHandler)
    delete (window as any).resizeHandler
  }
  
  // 清理滚动监听器
  if ((window as any).scrollHandler) {
    window.removeEventListener('scroll', (window as any).scrollHandler)
    window.removeEventListener('wheel', preventScrollEvent)
    window.removeEventListener('touchmove', preventScrollEvent)
    window.removeEventListener('keydown', preventKeyboardScroll)
    delete (window as any).scrollHandler
  }
  
  // 恢复页面滚动
  document.body.style.overflow = 'auto'
  
  // 清理resize防抖定时器
  if (resizeTimeout) {
    clearTimeout(resizeTimeout)
  }
  
  // 停止所有GSAP动画
  currentSigns.value.forEach(sign => {
    const element = signRefs.value[sign.id]
    if (element) {
      gsap.killTweensOf(element)
    }
    // 也清理可能的timeline动画
    const timeline = gsap.getById(`timeline-${sign.id}`)
    if (timeline) {
      timeline.kill()
    }
  })
  
})

</script>

<style scoped>
.metro-sign-generator {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  z-index: 1; /* 调整到正数，但低于内容层 */
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
  transition: opacity 0.5s ease-out, visibility 0.5s ease-out;
}

.metro-sign-generator.component-hidden {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

/* 中心标题样式 */
.center-title {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
  text-align: center;
  opacity: 0;
  transition: all 2s ease-out;
  pointer-events: none;
  max-width: 90vw; /* 限制最大宽度为视口宽度的90% */
  word-wrap: break-word; /* 在必要时允许单词断行 */
  hyphens: auto; /* 自动连字符 */
}

.center-title.fade-in {
  opacity: 1;
  animation: titleEntrance 2s ease-out forwards;
  pointer-events: auto;
  cursor: pointer;
}

.center-title.fade-in:hover {
  transform: translate(-50%, -50%) scale(1.05);
}

.center-title.fade-out {
  opacity: 0;
  animation: titleExit 0.5s ease-in forwards;
  pointer-events: none;
}

@keyframes titleExit {
  0% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9) translateY(-20px);
  }
}

@keyframes titleEntrance {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) translateY(30px) scale(0.9);
  }
  50% {
    opacity: 0.8;
    transform: translate(-50%, -50%) translateY(-10px) scale(1.05);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) translateY(0) scale(1);
  }
}

.center-title h1 {
  font-family: 'Noto Sans SC', "Microsoft YaHei", "SimHei", sans-serif;
  font-size: 4.5rem;
  font-weight: 900;
  color: #c62828;
  margin: 0 0 0.2rem 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.1em;
  background: linear-gradient(135deg, #d32f2f 0%, #c62828 50%, #b71c1c 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: titleScale 3s ease-in-out infinite;
  word-break: keep-all; /* 防止中文单词被断开 */
  overflow-wrap: normal; /* 正常换行 */
}

.center-title h2 {
  font-family: 'Noto Sans SC', "Microsoft YaHei", "SimHei", sans-serif;
  font-size: 2.8rem;
  font-weight: 700;
  color: #1565c0;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.05em;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: subtitleScale 4s ease-in-out infinite;
  word-break: keep-all; /* 防止中文单词被断开 */
  overflow-wrap: normal; /* 正常换行 */
}

@keyframes titleScale {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

@keyframes subtitleScale {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.015);
  }
}

.signs-flow-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  transition: filter 1.5s ease-out, opacity 1.5s ease-out;
}

.signs-flow-container.dimmed {
  filter: brightness(0.3) contrast(0.8);
  opacity: 0.6;
}

.signs-flow-container.accelerated {
  filter: brightness(0.2) blur(1px);
}

.sign-wrapper {
  position: absolute;
  opacity: 1;
  pointer-events: auto;
}

.metro-sign {
  border-radius: 8px; /* 稍微减小圆角 */
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6); /* 减小阴影 */
  cursor: pointer;
  transition: all 0.3s ease, opacity 0.5s ease; /* 添加透明度过渡 */
  position: relative;
  backdrop-filter: blur(2px); /* 减小模糊效果 */
  border: 1px solid rgba(255, 255, 255, 0.1); /* 减小边框宽度 */
}

.metro-sign:hover {
  transform: scale(1.15); /* 移除旋转，只保留缩放效果 */
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.8);
  z-index: 1000 !important;
  border-color: rgba(255, 255, 255, 0.3);
}

/* 确保所有标志可以重叠并且随机分布 */
.sign-wrapper {
  pointer-events: auto;
}

.metro-sign svg {
  overflow: visible;
}

/* 移除所有随机旋转效果，保持标志水平放置 */

/* 响应式设计 */
@media (max-width: 768px) {
  .center-title {
    width: 90%; /* 限制标题宽度 */
    padding: 0 15px; /* 添加内边距 */
    box-sizing: border-box;
  }
  
  .center-title h1 {
    font-size: 2.8rem; /* 稍微减小字体 */
    margin: 0 0 0.1rem 0;
    white-space: nowrap; /* 防止换行 */
    overflow: hidden; /* 隐藏溢出 */
    text-overflow: ellipsis; /* 超出部分用省略号显示 */
  }
  
  .center-title h2 {
    font-size: 1.8rem; /* 稍微减小字体 */
    white-space: nowrap; /* 防止换行 */
    overflow: hidden; /* 隐藏溢出 */
    text-overflow: ellipsis; /* 超出部分用省略号显示 */
  }
  
  .metro-sign {
    transform: scale(0.8); /* 平板设备稍微再缩小 */
    transform-origin: center;
  }
  
  .metro-sign:hover {
    transform: scale(0.95); /* 移除旋转，只保留缩放 */
  }
}

/* 中等屏幕（小平板、大手机横屏） */
@media (max-width: 640px) {
  .center-title {
    width: 95%; /* 进一步限制宽度 */
    padding: 0 12px;
  }
  
  .center-title h1 {
    font-size: 2.4rem;
    letter-spacing: 0.03em; /* 减少字间距 */
  }
  
  .center-title h2 {
    font-size: 1.5rem;
    letter-spacing: 0.02em; /* 减少字间距 */
  }
}

@media (max-width: 480px) {
  .center-title {
    width: 98%; /* 最大化利用宽度 */
    padding: 0 8px; /* 减少内边距 */
    box-sizing: border-box;
  }
  
  .center-title h1 {
    font-size: 2rem; /* 进一步减小字体 */
    margin: 0 0 0.05rem 0;
    letter-spacing: 0.01em; /* 减少字间距 */
    white-space: nowrap; /* 防止换行 */
    overflow: hidden; /* 隐藏溢出 */
    text-overflow: ellipsis; /* 超出部分用省略号显示 */
    line-height: 1.2; /* 确保行高合适 */
    min-width: 0; /* 允许收缩 */
  }
  
  .center-title h2 {
    font-size: 1.2rem; /* 进一步减小字体 */
    letter-spacing: 0; /* 移除字间距 */
    white-space: nowrap; /* 防止换行 */
    overflow: hidden; /* 隐藏溢出 */
    text-overflow: ellipsis; /* 超出部分用省略号显示 */
    line-height: 1.2; /* 确保行高合适 */
    min-width: 0; /* 允许收缩 */
  }
  
  .metro-sign {
    transform: scale(0.6); /* 手机设备更小 */
  }
  
  .metro-sign:hover {
    transform: scale(0.75); /* 移除旋转，只保留缩放 */
  }
}

/* 超小屏幕设备 */
@media (max-width: 360px) {
  .center-title {
    width: 100%;
    padding: 0 5px;
  }
  
  .center-title h1 {
    font-size: 1.8rem; /* 进一步减小 */
    letter-spacing: 0;
  }
  
  .center-title h2 {
    font-size: 1.1rem; /* 进一步减小 */
    letter-spacing: 0;
  }
}

/* 横屏手机优化 */
@media (max-height: 500px) and (orientation: landscape) {
  .center-title h1 {
    font-size: 1.8rem;
    margin: 0 0 0.02rem 0;
  }
  
  .center-title h2 {
    font-size: 1.1rem;
  }
}
</style>
